import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, CheckCircle, AlertCircle, Scale, Clock } from "lucide-react";
import SEO from "../components/SEO";
import LeadForm from "../components/forms/LeadForm";
import CTASection from "../components/sections/CTASection";
import { getAccidentType } from "../lib/api";
import config from "../config/stateConfig";

export default function AccidentTypeDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getAccidentType(slug).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 text-center text-charcoal-soft">Loading...</div>;
  if (!data) return (
    <div className="py-32 text-center">
      <h1 className="text-3xl font-display font-bold text-forest mb-3">Page not found</h1>
      <Link to="/accident-types" className="btn-primary">Back to all accident types</Link>
    </div>
  );

  const I = Icons[data.icon] || Icons.AlertTriangle;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": data.name + ` in ${config.stateName}`,
    "description": data.seo_description,
    "author": { "@type": "Organization", "name": config.brand },
  };

  return (
    <>
      <SEO title={data.seo_title || `${data.name} | ${config.brand}`} description={data.seo_description} schema={schema} />

      {/* Hero */}
      <section className="pt-12 md:pt-20 pb-12 md:pb-16 px-6 bg-bone">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <Link to="/accident-types" className="inline-flex items-center gap-2 text-sm font-semibold text-charcoal-soft hover:text-terracotta mb-6">
              ← All accident types
            </Link>
            <div className="inline-flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-terracotta text-white grid place-items-center">
                <I className="w-6 h-6" />
              </div>
              <div className="overline">{config.stateName} Accident Guide</div>
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold text-forest tracking-tight mb-5 text-balance">
              {data.name} in {config.stateName}
            </h1>
            <p className="text-lg md:text-2xl text-charcoal-soft leading-relaxed">
              {data.intro}
            </p>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <LeadForm compact sourcePage={`accident-type:${slug}`} />
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="px-6 py-12 md:py-16 bg-bone-alt">
        <div className="max-w-3xl mx-auto">
          <div className="overline mb-3">Overview</div>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-forest tracking-tight mb-5">What you should know</h2>
          <p className="text-base md:text-lg text-charcoal-soft leading-relaxed">{data.overview}</p>
        </div>
      </section>

      {/* Two columns: causes & injuries */}
      <section className="px-6 py-12 md:py-16">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
          <div>
            <div className="overline mb-2">Common Causes</div>
            <h3 className="text-2xl md:text-3xl font-display font-bold text-forest mb-5">What typically causes them</h3>
            <ul className="space-y-3">
              {(data.common_causes || []).map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-base text-charcoal-soft">
                  <AlertCircle className="w-5 h-5 text-terracotta shrink-0 mt-0.5" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline mb-2">Common Injuries</div>
            <h3 className="text-2xl md:text-3xl font-display font-bold text-forest mb-5">Injuries we see</h3>
            <ul className="space-y-3">
              {(data.common_injuries || []).map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-base text-charcoal-soft">
                  <CheckCircle className="w-5 h-5 text-forest shrink-0 mt-0.5" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What to do */}
      <section className="px-6 py-12 md:py-16 bg-forest text-bone">
        <div className="max-w-3xl mx-auto">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta mb-3">What to do</div>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-bone tracking-tight mb-8">
            Steps to take after a {data.name.toLowerCase()}
          </h2>
          <ol className="space-y-4">
            {(data.what_to_do || []).map((step, i) => (
              <li key={i} className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-full bg-terracotta text-white grid place-items-center font-display font-black text-base shrink-0">
                  {i + 1}
                </div>
                <p className="text-base md:text-lg text-bone/80 leading-relaxed pt-1">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Legal info */}
      <section className="px-6 py-12 md:py-16">
        <div className="max-w-3xl mx-auto space-y-10">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Scale className="w-5 h-5 text-terracotta" />
              <div className="overline">Legal Considerations</div>
            </div>
            <p className="text-base md:text-lg text-charcoal-soft leading-relaxed">{data.legal_considerations}</p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-terracotta" />
              <div className="overline">Compensation You May Recover</div>
            </div>
            <p className="text-base md:text-lg text-charcoal-soft leading-relaxed">{data.compensation_info}</p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-terracotta" />
              <div className="overline">Statute of Limitations</div>
            </div>
            <p className="text-base md:text-lg text-charcoal-soft leading-relaxed">{data.statute_of_limitations}</p>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="No time to lose"
        title={`Don't let a ${config.stateName} ${data.name.toLowerCase()} ruin your future.`}
        description={`The clock is ticking on ${config.stateName}'s 2-year statute of limitations. Get a free, no-obligation case review now.`}
      />
    </>
  );
}
